import React, { useState } from 'react';
import { Settings, Lock, Globe, Save, Eye, EyeOff, ArrowLeft, Check, AlertCircle, Shield, Zap } from 'lucide-react';
import { useNavigate } from "react-router-dom";
import Navbar from '../components/Navbar';


const Setting = () => {
  const [activeTab, setActiveTab] = useState('security');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [saved, setSaved] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [preferences, setPreferences] = useState({
    language: 'fr',
    timezone: 'africa/porto-novo',
    dateFormat: 'dd/mm/yyyy',
    currency: 'XOF'
  });

  const navigate = useNavigate();


  const validatePassword = () => {
    if (passwordData.newPassword.length < 8) {
      setPasswordError('Le mot de passe doit contenir au moins 8 caractères');
      return false;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('Les mots de passe ne correspondent pas');
      return false;
    }
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(passwordData.newPassword)) {
      setPasswordError('Le mot de passe doit contenir majuscules, minuscules et chiffres');
      return false;
    }
    setPasswordError('');
    return true;
  };

  const handleSave = async () => {
    if (activeTab === 'security') {
      if (passwordData.oldPassword && passwordData.newPassword) {
        console.log(passwordData.oldPassword, passwordData.newPassword, passwordData.confirmPassword);
        if (validatePassword()) {
          try {
            // Request api update password
            const token = localStorage.getItem('client_token');
            
            if (!token) {
              setPasswordError('Token d\'authentification manquant. Veuillez vous reconnecter.');
              return;
            }
            
            const response = await fetch('https://api-ec.cinef-expertises.com/api/clients/reset-password', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                old_password: passwordData.oldPassword,
                new_password: passwordData.newPassword,
                confirm_password: passwordData.confirmPassword
              })
            });

            if (!response.ok) {
              const errorData = await response.json();
              console.error('Erreur serveur:', errorData);
              setPasswordError(errorData.message || `Erreur ${response.status}: ${response.statusText}`);
              return;
            }

            const data = await response.json();
            console.log('Mot de passe mis à jour avec succès:', data);
          } catch (error) {
            console.error('Erreur réseau:', error);
            setPasswordError('Erreur de connexion. Vérifiez votre connexion internet.');
          }

          setSaved(true);
          setTimeout(() => {
            setSaved(false);
            setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
          }, 3000);
        }
      } else {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } else {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }
  };

  const getPasswordStrength = () => {
    const p = passwordData.newPassword;
    if (!p) return { label: '', color: '', width: '0%' };
    let s = 0;
    if (p.length >= 8) s++;
    if (p.length >= 12) s++;
    if (/[a-z]/.test(p) && /[A-Z]/.test(p) && /\d/.test(p)) s += 3;
    if (/[^a-zA-Z\d]/.test(p)) s++;
    if (s <= 2) return { label: 'Faible', color: '#ef4444', width: '33%' };
    if (s <= 4) return { label: 'Moyen', color: '#f59e0b', width: '66%' };
    return { label: 'Fort', color: '#10b981', width: '100%' };
  };

  const strength = getPasswordStrength();
  // Get user info from localStorage if needed
  // const userInfo = JSON.parse(localStorage.getItem('userInfo')) || {};

  return (
    <>
      <Navbar />
      <div className="settings-page">
        <div className="settings-container">
          <div className="page-header">
            <button
              className="back-button"
              onClick={() => navigate("/dashboard-user/echeances")}
            >
              <ArrowLeft size={20} />
              <span>Tableau de bord</span>
            </button>
          </div>


          <div className="settings-hero">
            <div className="hero-content">
              <div className="hero-icon"><Settings size={32} /></div>
              <div className="hero-text">
                <h1 className="page-title">Paramètres du compte</h1>
                <p className="page-subtitle">Gérez la sécurité et personnalisez votre expérience</p>
              </div>
            </div>
          </div>

          <div className="settings-layout">
            <div className="tabs-sidebar">
              <button className={`tab-btn ${activeTab === 'security' ? 'active' : ''}`} onClick={() => setActiveTab('security')}>
                <div className="tab-icon"><Shield size={22} /></div>
                <div className="tab-content">
                  <span className="tab-label">Sécurité</span>
                  <span className="tab-desc">Mot de passe et protection</span>
                </div>
              </button>
              {/* <button className={`tab-btn ${activeTab === 'preferences' ? 'active' : ''}`} onClick={() => setActiveTab('preferences')}>
                <div className="tab-icon"><Zap size={22} /></div>
                <div className="tab-content">
                  <span className="tab-label">Préférences</span>
                  <span className="tab-desc">Langue et formats</span>
                </div>
              </button> */}
            </div>

            <div className="settings-main">
              {activeTab === 'security' && (
                <div className="settings-section">
                  <div className="section-header">
                    <div className="section-icon-badge"><Lock size={24} /></div>
                    <div>
                      <h2 className="section-title">Sécurité du compte</h2>
                      <p className="section-description">Modifiez votre mot de passe pour sécuriser votre compte</p>
                    </div>
                  </div>

                  <div className="form-container">
                    <div className="form-group">
                      <label className="form-label">Mot de passe actuel</label>
                      <div className="input-wrapper">
                        <div className="input-icon"><Lock size={18} /></div>
                        <input type={showOldPassword ? "text" : "password"} className="form-input" placeholder="Entrez votre mot de passe actuel"
                          value={passwordData.oldPassword} onChange={(e) => setPasswordData({ ...passwordData, oldPassword: e.target.value })} />
                        <button className="toggle-password" onClick={() => setShowOldPassword(!showOldPassword)} type="button">
                          {showOldPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Nouveau mot de passe</label>
                      <div className="input-wrapper">
                        <div className="input-icon"><Lock size={18} /></div>
                        <input type={showNewPassword ? "text" : "password"} className="form-input" placeholder="Entrez votre nouveau mot de passe"
                          value={passwordData.newPassword} onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })} />
                        <button className="toggle-password" onClick={() => setShowNewPassword(!showNewPassword)} type="button">
                          {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      {passwordData.newPassword && (
                        <div className="password-strength">
                          <div className="strength-bar">
                            <div className="strength-fill" style={{ width: strength.width, background: strength.color }} />
                          </div>
                          <span className="strength-label" style={{ color: strength.color }}>{strength.label}</span>
                        </div>
                      )}
                      <p className="input-hint">Minimum 8 caractères avec majuscules, minuscules et chiffres</p>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Confirmer le nouveau mot de passe</label>
                      <div className="input-wrapper">
                        <div className="input-icon"><Lock size={18} /></div>
                        <input type={showConfirmPassword ? "text" : "password"} className="form-input" placeholder="Confirmez votre nouveau mot de passe"
                          value={passwordData.confirmPassword} onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })} />
                        <button className="toggle-password" onClick={() => setShowConfirmPassword(!showConfirmPassword)} type="button">
                          {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>

                    {passwordError && (
                      <div className="error-message">
                        <AlertCircle size={18} />
                        <span>{passwordError}</span>
                      </div>
                    )}

                    <div className="security-tips">
                      <div className="tips-header">
                        <Shield size={20} />
                        <h4>Conseils de sécurité</h4>
                      </div>
                      <ul className="tips-list">
                        <li><Check size={16} /><span>Utilisez un mot de passe unique</span></li>
                        <li><Check size={16} /><span>Évitez les informations personnelles</span></li>
                        <li><Check size={16} /><span>Changez régulièrement votre mot de passe</span></li>
                        <li><Check size={16} /><span>Ne partagez jamais votre mot de passe</span></li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'preferences' && (
                <div className="settings-section">
                  <div className="section-header">
                    <div className="section-icon-badge"><Globe size={24} /></div>
                    <div>
                      <h2 className="section-title">Préférences d'affichage</h2>
                      <p className="section-description">Personnalisez l'affichage selon vos préférences</p>
                    </div>
                  </div>

                  <div className="form-container">
                    <div className="preferences-grid">
                      <div className="preference-card">
                        <div className="card-icon">🌐</div>
                        <label className="form-label">Langue</label>
                        <select className="form-select" value={preferences.language} onChange={(e) => setPreferences({ ...preferences, language: e.target.value })}>
                          <option value="fr">🇫🇷 Français</option>
                          <option value="en">🇬🇧 English</option>
                        </select>
                      </div>

                      <div className="preference-card">
                        <div className="card-icon">💰</div>
                        <label className="form-label">Devise</label>
                        <select className="form-select" value={preferences.currency} onChange={(e) => setPreferences({ ...preferences, currency: e.target.value })}>
                          <option value="XOF">Franc CFA</option>
                          <option value="EUR">Euro</option>
                          <option value="USD">Dollar US</option>
                        </select>
                      </div>

                      <div className="preference-card">
                        <div className="card-icon">🕐</div>
                        <label className="form-label">Fuseau horaire</label>
                        <select className="form-select" value={preferences.timezone} onChange={(e) => setPreferences({ ...preferences, timezone: e.target.value })}>
                          <option value="africa/porto-novo">Porto-Novo (GMT+1)</option>
                          <option value="africa/abidjan">Abidjan (GMT+0)</option>
                          <option value="europe/paris">Paris (GMT+1)</option>
                        </select>
                      </div>

                      <div className="preference-card">
                        <div className="card-icon">📅</div>
                        <label className="form-label">Format de date</label>
                        <select className="form-select" value={preferences.dateFormat} onChange={(e) => setPreferences({ ...preferences, dateFormat: e.target.value })}>
                          <option value="dd/mm/yyyy">JJ/MM/AAAA</option>
                          <option value="mm/dd/yyyy">MM/JJ/AAAA</option>
                          <option value="yyyy-mm-dd">AAAA-MM-JJ</option>
                        </select>
                      </div>
                    </div>

                    <div className="preferences-info">
                      <div className="info-icon">💡</div>
                      <p>Ces paramètres seront appliqués à l'ensemble de votre compte.</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="settings-footer">
                <button className="save-button" onClick={handleSave}>
                  <Save size={20} />
                  <span>Enregistrer</span>
                </button>
                {saved && (
                  <div className="success-message">
                    <Check size={18} />
                    <span>Modifications enregistrées !</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Inter', -apple-system, sans-serif; -webkit-font-smoothing: antialiased; }
        .settings-page { min-height: 100vh; background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%); padding: 32px 24px; }
        .settings-container { max-width: 1200px; margin: 0 auto; }
        .page-header { margin-bottom: 32px; }
        .back-button { display: inline-flex; align-items: center; gap: 8px; padding: 12px 20px; background: white; border: 1px solid #e5e7eb;
          border-radius: 12px; font-size: 14px; font-weight: 500; color: #374151; cursor: pointer; transition: all 0.3s ease;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05); }
        .back-button:hover { background: #f9fafb; border-color: #008A44; color: #008A44; transform: translateX(-2px);
          box-shadow: 0 4px 12px rgba(0,138,68,0.1); }
        .settings-hero { background: linear-gradient(135deg, #008A44 0%, #006633 100%); border-radius: 20px; padding: 32px;
          margin-bottom: 32px; box-shadow: 0 10px 40px rgba(0,138,68,0.2); position: relative; overflow: hidden; }
        .settings-hero::before { content: ''; position: absolute; top: -50%; right: -10%; width: 400px; height: 400px;
          background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%); border-radius: 50%; }
        .hero-content { display: flex; align-items: center; gap: 20px; position: relative; z-index: 1; }
        .hero-icon { width: 64px; height: 64px; background: rgba(255,255,255,0.2); backdrop-filter: blur(10px);
          border-radius: 16px; display: flex; align-items: center; justify-content: center; color: white; flex-shrink: 0; }
        .hero-text { flex: 1; }
        .page-title { font-size: 32px; font-weight: 700; color: white; margin-bottom: 8px; letter-spacing: -0.5px; }
        .page-subtitle { font-size: 16px; color: rgba(255,255,255,0.9); font-weight: 500; }
        .settings-layout { display: grid; grid-template-columns: 280px 1fr; gap: 24px; }
        .tabs-sidebar { display: flex; flex-direction: column; gap: 12px; }
        .tab-btn { display: flex; align-items: center; gap: 16px; padding: 20px; background: white; border: 2px solid transparent;
          border-radius: 16px; cursor: pointer; transition: all 0.3s ease; text-align: left; box-shadow: 0 2px 8px rgba(0,0,0,0.04); }
        .tab-btn:hover { border-color: #e5e7eb; transform: translateX(4px); box-shadow: 0 4px 12px rgba(0,0,0,0.08); }
        .tab-btn.active { background: linear-gradient(135deg, #008A44 0%, #006633 100%); border-color: #008A44;
          box-shadow: 0 8px 24px rgba(0,138,68,0.2); transform: translateX(4px); }
        .tab-icon { width: 48px; height: 48px; background: #f3f4f6; border-radius: 12px; display: flex; align-items: center;
          justify-content: center; color: #6b7280; flex-shrink: 0; transition: all 0.3s ease; }
        .tab-btn.active .tab-icon { background: rgba(255,255,255,0.2); color: white; }
        .tab-content { display: flex; flex-direction: column; gap: 4px; }
        .tab-label { font-size: 15px; font-weight: 600; color: #374151; transition: color 0.3s ease; }
        .tab-btn.active .tab-label { color: white; }
        .tab-desc { font-size: 13px; color: #9ca3af; transition: color 0.3s ease; }
        .tab-btn.active .tab-desc { color: rgba(255,255,255,0.8); }
        .settings-main { background: white; border-radius: 20px; padding: 40px; box-shadow: 0 2px 12px rgba(0,0,0,0.05); }
        .settings-section { margin-bottom: 32px; }
        .section-header { display: flex; align-items: flex-start; gap: 20px; margin-bottom: 40px; padding-bottom: 24px;
          border-bottom: 2px solid #f3f4f6; }
        .section-icon-badge { width: 56px; height: 56px; background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
          border-radius: 14px; display: flex; align-items: center; justify-content: center; color: #008A44; flex-shrink: 0;
          box-shadow: 0 4px 12px rgba(0,138,68,0.1); }
        .section-title { font-size: 22px; font-weight: 700; color: #111827; margin-bottom: 6px; letter-spacing: -0.3px; }
        .section-description { font-size: 15px; color: #6b7280; line-height: 1.5; }
        .form-container { display: flex; flex-direction: column; gap: 28px; }
        .form-group { display: flex; flex-direction: column; gap: 10px; }
        .form-label { font-size: 14px; font-weight: 600; color: #374151; }
        .input-wrapper { position: relative; display: flex; align-items: center; }
        .input-icon { position: absolute; left: 16px; color: #9ca3af; display: flex; align-items: center; pointer-events: none; }
        .form-input, .form-select { width: 100%; padding: 14px 16px; border: 2px solid #e5e7eb; border-radius: 12px;
          font-size: 15px; color: #111827; background: #fafbfc; transition: all 0.3s ease; }
        .form-input { padding-left: 48px; padding-right: 48px; }
        .form-input:focus, .form-select:focus { outline: none; border-color: #008A44; background: white;
          box-shadow: 0 0 0 4px rgba(0,138,68,0.08); }
        .toggle-password { position: absolute; right: 16px; background: none; border: none; color: #9ca3af; cursor: pointer;
          padding: 4px; display: flex; align-items: center; transition: all 0.3s ease; border-radius: 6px; }
        .toggle-password:hover { color: #6b7280; background: #f3f4f6; }
        .password-strength { display: flex; align-items: center; gap: 12px; }
        .strength-bar { flex: 1; height: 6px; background: #e5e7eb; border-radius: 3px; overflow: hidden; }
        .strength-fill { height: 100%; transition: all 0.3s ease; border-radius: 3px; }
        .strength-label { font-size: 13px; font-weight: 600; }
        .input-hint { font-size: 13px; color: #6b7280; line-height: 1.5; }
        .security-tips { padding: 24px; background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%); border: 2px solid #86efac;
          border-radius: 14px; }
        .tips-header { display: flex; align-items: center; gap: 10px; margin-bottom: 16px; color: #008A44; }
        .tips-header h4 { font-size: 16px; font-weight: 700; color: #111827; }
        .tips-list { list-style: none; display: flex; flex-direction: column; gap: 12px; }
        .tips-list li { display: flex; align-items: flex-start; gap: 10px; font-size: 14px; color: #374151; line-height: 1.6; }
        .tips-list li svg { flex-shrink: 0; color: #008A44; margin-top: 2px; }
        .preferences-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; }
        .preference-card { padding: 24px; background: linear-gradient(135deg, #fafbfc 0%, #f3f4f6 100%); border: 2px solid #e5e7eb;
          border-radius: 14px; transition: all 0.3s ease; }
        .preference-card:hover { border-color: #008A44; box-shadow: 0 4px 12px rgba(0,138,68,0.08); }
        .card-icon { font-size: 32px; margin-bottom: 16px; }
        .preference-card .form-label { margin-bottom: 12px; }
        .preferences-info { display: flex; gap: 16px; padding: 20px; background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
          border: 2px solid #fde047; border-radius: 14px; }
        .info-icon { font-size: 24px; flex-shrink: 0; }
        .preferences-info p { font-size: 14px; color: #78350f; line-height: 1.6; }
        .error-message { display: flex; align-items: center; gap: 10px; padding: 14px 18px; background: #fef2f2;
          border: 2px solid #fecaca; border-radius: 12px; color: #dc2626; font-size: 14px; font-weight: 500; }
        .success-message { display: flex; align-items: center; gap: 10px; padding: 14px 20px;
          background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%); border: 2px solid #86efac; border-radius: 12px;
          color: #008A44; font-size: 14px; font-weight: 600; animation: slideIn 0.3s ease; }
        @keyframes slideIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
        .settings-footer { display: flex; align-items: center; gap: 20px; padding-top: 32px; border-top: 2px solid #f3f4f6; }
        .save-button { display: flex; align-items: center; gap: 10px; padding: 14px 32px;
          background: linear-gradient(135deg, #008A44 0%, #006633 100%); color: white; border: none; border-radius: 12px;
          font-size: 15px; font-weight: 600; cursor: pointer; transition: all 0.3s ease; box-shadow: 0 4px 12px rgba(0,138,68,0.2); }
        .save-button:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,138,68,0.3); }
        .save-button:active { transform: translateY(0); }
        @media (max-width: 1024px) {
          .settings-layout { grid-template-columns: 1fr; }
          .tabs-sidebar { flex-direction: row; overflow-x: auto; }
          .tab-btn { flex: 1; min-width: 200px; }
        }
        @media (max-width: 768px) {
          .settings-page { padding: 20px 16px; }
          .settings-main { padding: 24px 20px; }
          .preferences-grid { grid-template-columns: 1fr; }
          .hero-content { flex-direction: column; align-items: flex-start; }
          .page-title { font-size: 26px; }
        }
      `}</style>
    </>
  );
};

export default Setting;